import { useEffect, useState, useMemo } from "react";
import { useTenant } from "@/lib/tenant";
import { MapContainer, TileLayer, GeoJSON, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Plus, X, Save, Loader2, Calendar, MapPin, Trash2, ClipboardCheck, Clock, PlayCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  planned:     { cls: "bg-slate-500/20 text-slate-300 border-slate-500/30", label: "Planned" },
  in_progress: { cls: "bg-amber-500/20 text-amber-300 border-amber-500/30", label: "In progress" },
  completed:   { cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30", label: "Done" },
  cancelled:   { cls: "bg-red-500/20 text-red-300 border-red-500/30", label: "Cancelled" },
};

export default function Jobs() {
  const { api } = useTenant();
  const [jobs, setJobs]           = useState([]);
  const [operators, setOperators] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/jobs"),
      api.get("/admin/operators"),
    ]).then(([j, o]) => {
      setJobs(j.data || []);
      setOperators(o.data || []);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const onDelete = async (j) => {
    if (!window.confirm(`Delete job for ${j.assigned_operator_name}?`)) return;
    try { await api.delete(`/admin/jobs/${j.id}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-8 max-w-7xl">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold mb-1">Planned jobs</h1>
          <p className="text-sm text-slate-400">Assign work to operators — pick a field on the map so they know where to go.</p>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="plan-job"
                className="bg-emerald-500 hover:bg-emerald-400 text-[#0a0e1c] font-bold px-4 py-2 rounded-lg flex items-center gap-1.5">
          <Plus size={16} /> Plan a job
        </button>
      </div>

      {loading ? (
        <div className="text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</div>
      ) : !jobs.length ? (
        <div className="bg-[#0a0e1c] border border-white/10 rounded-lg p-10 text-center text-slate-500">
          No jobs planned yet — click "Plan a job".
        </div>
      ) : (
        <ul className="space-y-2">
          {jobs.map(j => {
            const s = STATUS_BADGE[j.status] || STATUS_BADGE.planned;
            return (
              <li key={j.id} className="bg-[#0a0e1c] border border-white/10 rounded-lg p-4 flex items-start gap-4" data-testid={`job-${j.id}`}>
                <div className="text-center shrink-0 w-16">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">
                    {j.planned_date ? new Date(j.planned_date + "T00:00:00").toLocaleDateString("en-GB", { month: "short" }).toUpperCase() : "—"}
                  </div>
                  <div className="text-2xl font-display font-black text-amber-300 tabular-nums">
                    {j.planned_date ? new Date(j.planned_date + "T00:00:00").getDate() : "—"}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-bold">{j.task || "(no task)"}</span>
                    <span className={`text-[10px] uppercase tracking-widest font-bold border px-1.5 py-0.5 rounded ${s.cls}`}>{s.label}</span>
                  </div>
                  <div className="text-xs text-slate-400 space-x-3">
                    <span><strong className="text-slate-300">Operator:</strong> {j.assigned_operator_name}</span>
                    {j.machine_label && <span><strong className="text-slate-300">Machine:</strong> {j.machine_label}</span>}
                    {j.client_name && <span><strong className="text-slate-300">Client:</strong> {j.client_name}</span>}
                  </div>
                  {j.field_name && (
                    <div className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                      <MapPin size={11} className="text-emerald-400" /> {j.field_name}
                    </div>
                  )}
                  {j.notes && <div className="text-xs text-slate-500 mt-1 italic">{j.notes}</div>}
                </div>
                <button onClick={() => onDelete(j)} className="text-slate-400 hover:text-red-300 p-2">
                  <Trash2 size={14} />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {showForm && <JobModal operators={operators} api={api}
                              onClose={() => setShowForm(false)}
                              onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function FitBounds({ geo }) {
  const map = useMap();
  useEffect(() => {
    if (!geo) return;
    try {
      const b = L.geoJSON(geo).getBounds();
      if (b.isValid()) map.fitBounds(b, { padding: [40, 40] });
    } catch { /* ignore */ }
  }, [geo, map]);
  return null;
}

function JobModal({ operators, onClose, onSaved, api }) {
  const [fields, setFields]     = useState([]);
  const [machines, setMachines] = useState([]);
  const [clients, setClients]   = useState([]);
  const [tasks, setTasks]       = useState([]);
  const [form, setForm] = useState({
    planned_date: new Date().toISOString().slice(0, 10),
    assigned_operator_id: "", machine_vin: null, machine_label: "",
    client_id: null, client_name: "",
    task: "", field_id: null, field_name: "", field_centroid: null, notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/fieldops/fields").catch(() => ({ data: { data: [] } })),
      api.get("/fieldops/fleet").catch(() => ({ data: { data: [] } })),
      api.get("/admin/machines"),
      api.get("/app/diary/tasks").catch(() => ({ data: ["Mowing","Ploughing","Spraying","Baling","Carting","Other"] })),
    ]).then(([f, fl, mm, t]) => {
      setFields(f.data?.data || []);
      const flList = Array.isArray(fl.data) ? fl.data : (fl.data?.data || []);
      const manual = (mm.data || []).filter(m => m.active).map(m => ({ vin: m.vin, label: m.label, model: m.model }));
      setMachines([
        ...flList.map(m => ({ vin: m.vin, label: m.nickname || m.vin, model: m.model })),
        ...manual,
      ]);
      setTasks(t.data || []);
      // Best-effort clients (works only if Xero connected — fallback to empty)
      // No-op for now; users can type client manually.
      setClients([]);
    }).finally(() => setLoading(false));
  }, [api]);

  const geo = useMemo(() => {
    const features = [];
    for (const f of fields) {
      for (const b of f.boundaries || []) {
        if (!b.coordinates) continue;
        features.push({
          type: "Feature",
          properties: { field_id: f.field_id, name: f.field_name, area_ha: f.area_ha, farm: f.farm },
          geometry: { type: b.type || "Polygon", coordinates: b.coordinates },
        });
      }
    }
    return features.length ? { type: "FeatureCollection", features } : null;
  }, [fields]);

  const onFieldClick = (feature, layer) => {
    layer.on({
      click: () => {
        // Compute centroid roughly = first coord ring's first point
        const ring = feature.geometry.coordinates?.[0];
        const centroid = ring && ring[0] ? { lon: ring[0][0], lat: ring[0][1] } : null;
        setForm(f => ({
          ...f,
          field_id: feature.properties.field_id,
          field_name: feature.properties.name,
          field_centroid: centroid,
        }));
      },
    });
    layer.bindTooltip(`${feature.properties.name}${feature.properties.area_ha ? ` · ${feature.properties.area_ha} ha` : ""}`, { sticky: true });
  };

  const fieldStyle = (feature) => {
    const isSelected = form.field_id === feature.properties.field_id;
    return {
      color: isSelected ? "#10b981" : "#ffffff",
      weight: isSelected ? 3 : 2,
      fillColor: isSelected ? "#10b981" : "#fbbf24",
      fillOpacity: isSelected ? 0.4 : 0.15,
    };
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.assigned_operator_id) { setErr("Pick an operator"); return; }
    if (!form.task) { setErr("Pick a task"); return; }
    setBusy(true); setErr(null);
    try {
      const op = operators.find(o => o.id === form.assigned_operator_id);
      await api.post("/admin/jobs", { ...form, assigned_operator_name: op?.name || "" });
      toast.success("Job planned");
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm grid place-items-center z-50 p-4" onClick={onClose}>
      <div className="bg-[#0b1020] border border-white/10 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0b1020] border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">Plan a job</h3>
          <button onClick={onClose}><X size={18} className="text-slate-400" /></button>
        </div>

        <div className="p-6 grid md:grid-cols-2 gap-5">
          {/* Map column */}
          <div>
            <label className="block text-xs uppercase tracking-widest text-slate-400 font-bold mb-2">
              Pick a field on the map
            </label>
            <div className="h-[460px] rounded-lg overflow-hidden border border-white/10 bg-slate-900">
              {loading ? (
                <div className="h-full grid place-items-center text-slate-500"><Loader2 className="animate-spin" /></div>
              ) : !geo ? (
                <div className="h-full grid place-items-center text-slate-500 text-sm text-center px-6">
                  No fields imported yet.<br/>Open <code>/admin/fleet/map</code> and let it load, then come back.
                </div>
              ) : (
                <MapContainer center={[57.243, -2.495]} zoom={11} scrollWheelZoom style={{ height: "100%" }}>
                  <TileLayer
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                    attribution='Tiles © Esri' maxZoom={19}
                  />
                  <FitBounds geo={geo} />
                  <GeoJSON key={(geo.features.length) + form.field_id} data={geo}
                            style={fieldStyle} onEachFeature={onFieldClick} />
                </MapContainer>
              )}
            </div>
            {form.field_name && (
              <div className="mt-2 text-xs flex items-center gap-2">
                <MapPin size={12} className="text-emerald-400" />
                <strong className="text-emerald-300">{form.field_name}</strong>
                <button onClick={() => setForm(f => ({ ...f, field_id: null, field_name: "", field_centroid: null }))}
                        className="text-slate-400 hover:text-red-300 ml-auto text-xs">Clear</button>
              </div>
            )}
          </div>

          {/* Form column */}
          <div className="space-y-3">
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-400 font-bold mb-1.5">
                <Calendar size={11} className="inline mr-1" /> Date
              </label>
              <input type="date" value={form.planned_date}
                     onChange={e => setField("planned_date", e.target.value)} data-testid="job-date"
                     className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-400 font-bold mb-1.5">Assign to</label>
              <select value={form.assigned_operator_id}
                      onChange={e => setField("assigned_operator_id", e.target.value)} data-testid="job-operator"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white">
                <option value="">— pick operator —</option>
                {operators.filter(o => o.active).map(o => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-400 font-bold mb-1.5">Task</label>
              <select value={form.task} onChange={e => setField("task", e.target.value)} data-testid="job-task"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white">
                <option value="">— pick task —</option>
                {tasks.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-400 font-bold mb-1.5">Machine</label>
              <select value={form.machine_vin || ""} onChange={e => {
                const m = machines.find(x => x.vin === e.target.value);
                setForm(f => ({ ...f, machine_vin: m?.vin || null, machine_label: m?.label || "" }));
              }} data-testid="job-machine"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white">
                <option value="">— pick machine —</option>
                {machines.map(m => <option key={m.vin} value={m.vin}>{m.label} {m.model ? `· ${m.model}` : ""}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-400 font-bold mb-1.5">Customer (optional)</label>
              <input value={form.client_name} onChange={e => setField("client_name", e.target.value)}
                     placeholder="Type customer name"
                     className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-slate-400 font-bold mb-1.5">Notes / instructions</label>
              <textarea value={form.notes} onChange={e => setField("notes", e.target.value)} rows={3}
                        placeholder="e.g. Drop trailer at gate, check oil before starting…"
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
            </div>
            {err && <div className="text-sm text-red-300">{err}</div>}
            <div className="flex gap-2 pt-2">
              <button onClick={onClose} className="flex-1 px-4 py-2 rounded-lg bg-white/5 text-slate-300">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="job-save"
                      className="flex-1 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-[#0a0e1c] font-bold px-4 py-2 rounded-lg flex items-center justify-center gap-1.5">
                {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} Plan job
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
